import { sizer } from "layout/styles/styled/sizer";
import { useMemo, useState } from "react";
import {
  IAthenaImportResult,
  IAthenaImportRowResult,
} from "store/queries/integration";
import styled from "styled-components";

/**
 * The per-appointment outcome of one Athena import: the summary cards and their drill-down lists.
 *
 * Rendered by the "Import from Athena" dialog once an import finishes, and by Integrations → Athena
 * (CLE-2948) for any past import. `results` carries patient names, so it only ever reaches this
 * component from `GET /integration/athena/import/:importId`.
 */

type ExpandedSection =
  | "created"
  | "updated"
  | "duplicates"
  | "unmappedPayers"
  | "noFacility"
  | "noCpt"
  | "medicareSupplement"
  | "errors"
  | null;

export const FileInfo = styled.span`
  font-size: 12px;
  color: var(--text-color-secondary);
`;

const ResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizer(3)};
`;

const ResultsSummary = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: ${sizer(2)};
`;

type SummaryCardVariant = "success" | "error" | "warning" | "info" | "neutral";

const SummaryCard = styled.div<{
  variant?: SummaryCardVariant;
  clickable?: boolean;
  expanded?: boolean;
}>`
  padding: ${sizer(2)};
  border-radius: 4px;
  text-align: center;
  cursor: ${({ clickable }) => (clickable ? "pointer" : "default")};
  transition: all 0.15s ease;
  border: 2px solid transparent;
  background-color: ${({ variant }) => {
    switch (variant) {
      case "success":
        return "var(--green-50)";
      case "error":
        return "var(--red-50)";
      case "warning":
        return "var(--yellow-50)";
      default:
        return "var(--surface-ground)";
    }
  }};

  ${({ clickable }) =>
    clickable &&
    `
    &:hover {
      filter: brightness(0.95);
    }
  `}

  ${({ expanded, variant }) => {
    if (!expanded) return "";
    const borderColors: Record<string, string> = {
      success: "var(--green-400)",
      error: "var(--red-400)",
      warning: "var(--yellow-400)",
    };
    return `border-color: ${
      (variant && borderColors[variant]) || "var(--primary-400)"
    };`;
  }}

  .count {
    font-size: 24px;
    font-weight: bold;
    color: ${({ variant }) => {
      switch (variant) {
        case "success":
          return "var(--green-600)";
        case "error":
          return "var(--red-600)";
        case "warning":
          return "var(--yellow-600)";
        default:
          return "var(--text-color)";
      }
    }};
  }

  .label {
    font-size: 12px;
    color: var(--text-color-secondary);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
  }

  .expand-icon {
    font-size: 10px;
  }
`;

const DetailItem = styled.div`
  padding: ${sizer(2)};
  border-bottom: 1px solid var(--surface-border);
  font-size: 13px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${sizer(2)};

  &:last-child {
    border-bottom: none;
  }

  .patient-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .patient-name {
    font-weight: 600;
  }

  .dos {
    color: var(--text-color-secondary);
    font-size: 12px;
  }

  .account-name {
    color: var(--text-color-secondary);
    font-size: 11px;
    font-style: italic;
  }

  .error-message {
    color: var(--red-600);
    font-size: 12px;
    max-width: 300px;
    word-break: break-word;
  }
`;

const DetailList = styled.div`
  max-height: 250px;
  overflow-y: auto;
  border: 1px solid var(--surface-border);
  border-radius: 4px;
  background-color: var(--surface-ground);
`;

const DetailSection = styled.div`
  margin-top: ${sizer(2)};
`;

const DetailHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${sizer(1)};

  .section-title {
    font-weight: 600;
    font-size: 13px;
    color: var(--text-color-secondary);
  }
`;

const InfoBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${sizer(2)};
  padding: ${sizer(3)};
  background-color: var(--blue-50);
  border: 1px solid var(--blue-200);
  border-radius: 4px;
  font-size: 13px;
  color: var(--blue-700);

  .pi-info-circle {
    margin-top: 2px;
    flex-shrink: 0;
  }
`;

interface IAthenaImportResultsProps {
  importResult: IAthenaImportResult;
  /** The dialog shows this right after an import; a past import's log view has no use for it. */
  showEligibilityNote?: boolean;
}

export function AthenaImportResults({
  importResult,
  showEligibilityNote = true,
}: IAthenaImportResultsProps) {
  const [expandedSection, setExpandedSection] = useState<ExpandedSection>(null);

  const categorizedResults = useMemo(() => {
    const created: IAthenaImportRowResult[] = [];
    const updated: IAthenaImportRowResult[] = [];
    const duplicates: IAthenaImportRowResult[] = [];
    const unmappedPayers: IAthenaImportRowResult[] = [];
    const noFacility: IAthenaImportRowResult[] = [];
    const noCpt: IAthenaImportRowResult[] = [];
    const medicareSupplement: IAthenaImportRowResult[] = [];
    const errors: IAthenaImportRowResult[] = [];

    (importResult.results || []).forEach((row) => {
      if (row.success && row.updated) {
        updated.push(row);
      } else if (row.success && !row.updated) {
        created.push(row);
      } else if (
        row.skipped &&
        (row.errors?.ALREADY_CURRENT || row.errors?.DUPLICATE)
      ) {
        duplicates.push(row);
      } else if (row.errors?.UNMAPPED_PAYER) {
        unmappedPayers.push(row);
      } else if (row.errors?.NO_FACILITY) {
        // CLE-2633 — these are deliberate skips, not failures. Without their own buckets they
        // landed in `errors`, so the drill-down disagreed with the Errors count from the API.
        noFacility.push(row);
      } else if (row.errors?.NO_CPT) {
        noCpt.push(row);
      } else if (row.errors?.MEDICARE_SUPPLEMENT) {
        // CLE-2674 — also a deliberate skip, so it needs its own bucket for the same reason.
        medicareSupplement.push(row);
      } else if (!row.success && row.errors) {
        errors.push(row);
      }
    });

    return {
      created,
      updated,
      duplicates,
      unmappedPayers,
      noFacility,
      noCpt,
      medicareSupplement,
      errors,
    };
  }, [importResult.results]);

  const toggleSection = (section: ExpandedSection) => {
    setExpandedSection((prev) => (prev === section ? null : section));
  };

  const renderSummaryCard = (
    label: string,
    count: number,
    variant: SummaryCardVariant,
    section: ExpandedSection,
    hasItems: boolean
  ) => (
    <SummaryCard
      variant={variant}
      clickable={hasItems}
      expanded={expandedSection === section}
      onClick={() => hasItems && toggleSection(section)}
    >
      <div className="count">{count}</div>
      <div className="label">
        {label}
        {hasItems && (
          <i
            className={`pi pi-chevron-${
              expandedSection === section ? "up" : "down"
            } expand-icon`}
          />
        )}
      </div>
    </SummaryCard>
  );

  const renderDetailList = (
    title: string,
    rows: IAthenaImportRowResult[],
    showErrors?: boolean
  ) => (
    <DetailSection>
      <DetailHeader>
        <span className="section-title">
          {title} ({rows.length})
        </span>
      </DetailHeader>
      <DetailList>
        {rows.map((row, index) => (
          <DetailItem key={index}>
            <div className="patient-info">
              <span className="patient-name">{row.patientName}</span>
              <span className="dos">DOS: {row.dos}</span>
              <span className="account-name">{row.accountName}</span>
              {showErrors && row.errors && (
                <span className="error-message">
                  {Object.values(row.errors).join("; ")}
                </span>
              )}
            </div>
          </DetailItem>
        ))}
      </DetailList>
    </DetailSection>
  );

  const hasCreated = categorizedResults.created.length > 0;
  const hasUpdated = categorizedResults.updated.length > 0;
  const hasDuplicates = categorizedResults.duplicates.length > 0;
  const hasUnmappedPayers = categorizedResults.unmappedPayers.length > 0;
  const hasNoFacility = categorizedResults.noFacility.length > 0;
  const hasNoCpt = categorizedResults.noCpt.length > 0;
  const hasMedicareSupplement =
    categorizedResults.medicareSupplement.length > 0;
  const hasErrors = categorizedResults.errors.length > 0;

  return (
    <ResultsContainer>
      <ResultsSummary>
        <SummaryCard variant="info">
          <div className="count">{importResult.totalGroups}</div>
          <div className="label">Groups</div>
        </SummaryCard>
        {renderSummaryCard(
          "Created",
          importResult.successCount,
          "success",
          "created",
          hasCreated
        )}
        {renderSummaryCard(
          "Updated",
          importResult.updateCount,
          "success",
          "updated",
          hasUpdated
        )}
        {renderSummaryCard(
          "Duplicates",
          importResult.duplicateCount,
          "warning",
          "duplicates",
          hasDuplicates
        )}
        {renderSummaryCard(
          "Unmapped",
          importResult.unmappedPayerCount,
          "error",
          "unmappedPayers",
          hasUnmappedPayers
        )}
        {renderSummaryCard(
          "No facility",
          importResult.noFacilityCount,
          "warning",
          "noFacility",
          hasNoFacility
        )}
        {renderSummaryCard(
          "No CPT",
          importResult.noCptCount,
          "warning",
          "noCpt",
          hasNoCpt
        )}
        {renderSummaryCard(
          "Medicare + supplement",
          importResult.medicareSupplementCount,
          "warning",
          "medicareSupplement",
          hasMedicareSupplement
        )}
        {renderSummaryCard(
          "Errors",
          importResult.errorCount,
          "error",
          "errors",
          hasErrors
        )}
      </ResultsSummary>

      {expandedSection === "created" &&
        hasCreated &&
        renderDetailList("Created Estimates", categorizedResults.created)}

      {expandedSection === "updated" &&
        hasUpdated &&
        renderDetailList("Updated Estimates", categorizedResults.updated)}

      {expandedSection === "duplicates" &&
        hasDuplicates &&
        renderDetailList("Duplicate Estimates", categorizedResults.duplicates)}

      {expandedSection === "unmappedPayers" &&
        hasUnmappedPayers &&
        renderDetailList(
          "Unmapped Payers",
          categorizedResults.unmappedPayers,
          true
        )}

      {expandedSection === "noFacility" &&
        hasNoFacility &&
        renderDetailList(
          "Skipped - no sending facility",
          categorizedResults.noFacility,
          true
        )}

      {expandedSection === "noCpt" &&
        hasNoCpt &&
        renderDetailList(
          "Skipped - no CPT code",
          categorizedResults.noCpt,
          true
        )}

      {expandedSection === "medicareSupplement" &&
        hasMedicareSupplement &&
        renderDetailList(
          "Skipped - Medicare with a supplement (no patient balance)",
          categorizedResults.medicareSupplement,
          true
        )}

      {expandedSection === "errors" &&
        hasErrors &&
        renderDetailList("Errors", categorizedResults.errors, true)}

      {showEligibilityNote &&
        (importResult.successCount > 0 || importResult.updateCount > 0) && (
          <InfoBox>
            <i className="pi pi-info-circle" />
            <span>
              Eligibility checks are running in the background. Estimates will
              show updated benefit information once processing is complete.
            </span>
          </InfoBox>
        )}

      {importResult.totalRows !== importResult.totalGroups && (
        <FileInfo>
          Note: {importResult.totalRows} rows were collapsed into{" "}
          {importResult.totalGroups} patient groups (multiple CPT codes per
          patient).
        </FileInfo>
      )}
    </ResultsContainer>
  );
}
